import React, { useState, useEffect, useImperativeHandle } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  Alert,
  Linking,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button, Toggle } from '../design-system';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { colors, spacing, typography } from '../../theme';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { DatePickerField } from '../DatePickerField';
import { ReminderPicker } from './ReminderPicker';
import { AttendeeStatusBadge, AttendeeRsvpStatus } from './AttendeeStatusBadge';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';
import { firstText } from '../../utils/contactIdentifiers';

export interface EventAttendee {
  _id?: string;
  type: 'user' | 'department' | 'external';
  userId?: string;
  departmentId?: string;
  external?: {
    name: string;
    email: string;
    phone?: string;
    contactId?: string;
  };
  role: 'organizer' | 'required' | 'optional';
  status: AttendeeRsvpStatus;
  responseAt?: Date;
  notificationSent: boolean;
}

export interface EventAttachment {
  _id?: string;
  name: string;
  type: string;
  size: number;
  url: string;
  storagePath?: string;
  uploadedBy?: string;
  uploadedAt?: Date;
}

export interface EventNote {
  _id?: string;
  content: string;
  createdBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
  isPrivate?: boolean;
}

export interface EventFormData {
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  timezone: string;
  isAllDay?: boolean;
  meetingType?: 'online' | 'offline' | 'hybrid';
  location?: {
    name?: string;
    address?: string;
    city?: string;
    country?: string;
  };
  onlineMeeting?: {
    provider: 'zoom' | 'google_meet' | 'microsoft_teams' | 'custom';
    joinUrl: string;
    password?: string;
  };
  eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  attachments?: EventAttachment[];
  notes?: EventNote[];
  reminders?: number[];
  attendees?: EventAttendee[];
}

export interface EventFormProps {
  initialData?: Partial<EventFormData>;
  onSubmit: (data: EventFormData) => void;
  onCancel: () => void;
  submitLabel?: string;
  loading?: boolean;
  calendarId: string;
  businessId: string;
  eventId?: string; // Event ID for attachment uploads (if editing existing event)
  calendars?: any[]; // List of calendars for selection
  onCalendarChange?: (calendarId: string) => void; // Callback when calendar is changed
  hideActions?: boolean; // Hide bottom action buttons (for web header actions)
  /** The selected calendar's slot duration (minutes), used to default a NEW event's
   * start/end instead of a flat 1-hour guess — omitted/undefined falls back to 1 hour. */
  slotDurationMinutes?: number;
}

export const EventForm = React.forwardRef<{ submit: () => void }, EventFormProps>(({
  initialData,
  onSubmit,
  onCancel,
  submitLabel,
  loading = false,
  calendarId,
  businessId,
  eventId,
  calendars = [],
  onCalendarChange,
  hideActions = false,
  slotDurationMinutes,
}, ref) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const resolvedSubmitLabel = submitLabel ?? t('calendar.eventForm.defaultSubmitLabel');
  // Slot duration (from the calendar's own configured booking-slot length) drives
  // the default new-event length instead of a flat 1-hour guess, when configured.
  const durationMs = (slotDurationMinutes && slotDurationMinutes > 0 ? slotDurationMinutes : 60) * 60 * 1000;
  const now = new Date();
  const defaultStartDate = initialData?.startDate || new Date(now.getTime() + 60 * 60 * 1000); // pre-filled date (e.g. tapped on the calendar) or 1 hour from now
  const defaultEndDate = initialData?.endDate || new Date(defaultStartDate.getTime() + durationMs);

  const [formData, setFormData] = useState<EventFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    startDate: initialData?.startDate || defaultStartDate,
    endDate: initialData?.endDate || defaultEndDate,
    timezone: initialData?.timezone || 'UTC',
    isAllDay: initialData?.isAllDay || false,
    meetingType: initialData?.meetingType || 'offline',
    location: initialData?.location || {},
    onlineMeeting: initialData?.onlineMeeting || {
      provider: 'custom',
      joinUrl: '',
    },
    eventStatus: initialData?.eventStatus || 'scheduled',
    attachments: initialData?.attachments || [],
    notes: initialData?.notes || [],
    reminders: initialData?.reminders || [],
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [contacts, setContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  // Existing attendees keyed by contact id — preserves status/role/notificationSent
  // for guests that were already invited, so re-saving the form doesn't reset
  // their RSVP back to 'pending'. Only contacts newly added this session (not
  // present here) get a fresh 'pending' attendee synthesized on submit.
  const [existingAttendeesByContactId, setExistingAttendeesByContactId] = useState<Record<string, EventAttendee>>({});
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>(calendarId);
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, [businessId]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        startDate: initialData.startDate || defaultStartDate,
        endDate: initialData.endDate || defaultEndDate,
        timezone: initialData.timezone || 'UTC',
        isAllDay: initialData.isAllDay || false,
        meetingType: initialData.meetingType || 'offline',
        location: initialData.location || {},
        onlineMeeting: initialData.onlineMeeting || {
          provider: 'custom',
          joinUrl: '',
        },
        eventStatus: initialData.eventStatus || 'scheduled',
        attachments: initialData.attachments || [],
        notes: initialData.notes || [],
        reminders: initialData.reminders || [],
      });

      // Extract contact IDs from existing attendees (external type), and keep the
      // full attendee record (status/role/notificationSent) keyed by contactId so
      // handleSubmit can preserve it instead of resetting every guest to 'pending'.
      if (initialData.attendees) {
        const externalAttendees = initialData.attendees.filter(
          (a: any) => a.type === 'external' && a.external?.contactId
        );
        setSelectedContactIds(externalAttendees.map((a: any) => a.external.contactId));
        const byContactId: Record<string, EventAttendee> = {};
        for (const a of externalAttendees) {
          const contactId: string | undefined = a.external?.contactId;
          if (contactId) byContactId[contactId] = a;
        }
        setExistingAttendeesByContactId(byContactId);
      }
    }
  }, [initialData]);

  const loadContacts = async () => {
    if (!businessId) return;
    
    try {
      setLoadingContacts(true);
      const response = await apiService.getUnifiedContacts({ limit: 1000 });
      if (response.statusCode === 1000 && response.data) {
        const contactsData = response.data.contacts || response.data || [];
        // Contacts can hold several phones/emails (lists) — the first as text,
        // for search, display and attendee details
        setContacts((Array.isArray(contactsData) ? contactsData : []).map((c: any) => ({
          ...c,
          name: String(c.name || c.profile?.name || ''),
          email: firstText(c.email),
          phoneNumber: firstText(c.phoneNumber || c.phone),
        })));
      }
    } catch (error: any) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('calendar.eventForm.titleRequired');
    }

    // Validate start date/time is before end date/time
    if (formData.startDate >= formData.endDate) {
      if (formData.isAllDay) {
        newErrors.endDate = t('calendar.eventForm.endDateAfterStartDate');
      } else {
        // Check if same day but end time is before/equal to start time
        const startDateOnly = new Date(formData.startDate);
        startDateOnly.setHours(0, 0, 0, 0);
        const endDateOnly = new Date(formData.endDate);
        endDateOnly.setHours(0, 0, 0, 0);

        if (startDateOnly.getTime() === endDateOnly.getTime()) {
          newErrors.endDate = t('calendar.eventForm.endTimeAfterStartTime');
        } else {
          newErrors.endDate = t('calendar.eventForm.endDateTimeAfterStart');
        }
      }
    }

    if (formData.meetingType === 'online' || formData.meetingType === 'hybrid') {
      if (!formData.onlineMeeting?.joinUrl) {
        newErrors.onlineMeetingUrl = t('calendar.eventForm.meetingUrlRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    // Update selectedCalendarId when calendarId prop changes
    if (calendarId) {
      setSelectedCalendarId(calendarId);
    }
  }, [calendarId]);

  const handleSubmit = () => {
    if (validate()) {
      if (!selectedCalendarId) {
        Alert.alert(t('common.error'), t('calendar.eventForm.selectCalendarError'));
        return;
      }

      // Convert selected contacts to EventAttendee format — reuse the existing
      // attendee record (status/role/notificationSent) for guests that were
      // already invited, so re-saving doesn't reset their RSVP back to
      // 'pending'; only genuinely new guests get a fresh pending attendee.
      const attendeeContacts: EventAttendee[] = contacts
        .filter((contact) => selectedContactIds.includes(contact.id))
        .map((contact) => {
          const existing = existingAttendeesByContactId[contact.id];
          if (existing) return existing;
          return {
            type: 'external' as const,
            external: {
              name: contact.name || 'Unknown',
              email: contact.email || contact.phoneNumber || '',
              phone: contact.phoneNumber || '',
              contactId: contact.id,
            },
            role: 'optional' as const,
            status: 'pending' as const,
            notificationSent: false,
          };
        });

      // Get timezone from selected calendar
      const selectedCalendar = calendars.find((c: any) => c._id === selectedCalendarId);
      const calendarTimezone = selectedCalendar?.timezone || formData.timezone || 'UTC';

      // Merge formData with attendees, calendarId, and timezone from calendar
      const formDataWithAttendees = {
        ...formData,
        calendarId: selectedCalendarId,
        timezone: calendarTimezone, // Use calendar's timezone
        attendees: attendeeContacts,
      };

      onSubmit(formDataWithAttendees);
    }
  };

  // Expose submit function via ref
  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  const updateField = (field: keyof EventFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const updateLocationField = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
  };

  const updateOnlineMeetingField = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      onlineMeeting: { ...prev.onlineMeeting, [field]: value } as any,
    }));
  };

  const handlePickAttachment = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      setUploadingAttachment(true);

      try {
        let uploadResponse;
        
        // If eventId exists, use calendar-specific upload endpoint
        if (eventId) {
          uploadResponse = await apiService.uploadCalendarAttachment(
            eventId,
            asset.uri,
            asset.name || `attachment_${Date.now()}`,
            asset.mimeType || 'application/octet-stream'
          );
        } else {
          // For new events, use generic media upload (will be moved to calendar folder after event creation)
          uploadResponse = await apiService.uploadMedia(
            asset.uri,
            asset.name || `attachment_${Date.now()}`,
            asset.mimeType || 'application/octet-stream'
          );
        }

        if (uploadResponse.statusCode === 1000 && uploadResponse.data && uploadResponse.data.url) {
          const newAttachment: EventAttachment = {
            name: uploadResponse.data.name || asset.name,
            type: uploadResponse.data.type || asset.mimeType || 'application/octet-stream',
            size: uploadResponse.data.size || asset.size || 0,
            url: uploadResponse.data.url,
            storagePath: uploadResponse.data.storagePath,
            uploadedBy: uploadResponse.data.uploadedBy,
            uploadedAt: uploadResponse.data.uploadedAt ? new Date(uploadResponse.data.uploadedAt) : new Date(),
          };

          updateField('attachments', [...(formData.attachments || []), newAttachment]);
          // Clear any attachment-related errors after successful upload
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.attachments;
            return newErrors;
          });
        } else {
          Alert.alert(t('common.error'), uploadResponse.errors?.[0]?.message || t('calendar.eventForm.uploadAttachmentFailed'));
        }
      } catch (error: any) {
        console.error('Error uploading attachment:', error);
        Alert.alert(t('common.error'), error.message || t('calendar.eventForm.uploadAttachmentFailed'));
      } finally {
        setUploadingAttachment(false);
      }
    } catch (error: any) {
      console.error('Error picking attachment:', error);
      Alert.alert(t('common.error'), t('calendar.eventForm.pickAttachmentFailed'));
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = (formData.attachments || []).filter((_, i) => i !== index);
    updateField('attachments', newAttachments);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const newNoteObj: EventNote = {
        content: newNote.trim(),
        isPrivate: false,
        createdAt: new Date(),
      };
      updateField('notes', [...(formData.notes || []), newNoteObj]);
      setNewNote('');
    }
  };

  const handleUpdateNote = (index: number, content: string) => {
    const updatedNotes = (formData.notes || []).map((note, i) => 
      i === index ? { ...note, content, updatedAt: new Date() } : note
    );
    updateField('notes', updatedNotes);
  };

  const handleRemoveNote = (index: number) => {
    const newNotes = (formData.notes || []).filter((_, i) => i !== index);
    updateField('notes', newNotes);
  };

  const getAttachmentUrl = (url: string) => {
    if (url.includes('firebasestorage.googleapis.com')) {
      try {
        // Use proxy endpoint for Firebase URLs
        return `${apiService.baseURL}/api/storage/media/proxy?url=${encodeURIComponent(url)}`;
      } catch {
        return url;
      }
    }
    return url;
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.form}>
        {/* Title */}
        <View style={styles.field}>
          <Input
            label={t('calendar.eventForm.titleLabel')}
            value={formData.title}
            onChangeText={(text) => updateField('title', text)}
            placeholder={t('calendar.eventForm.titlePlaceholder')}
            error={errors.title}
            autoCapitalize="words"
            maxLength={500}
          />
        </View>

        {/* Calendar Selection - Only show in create mode or if calendars provided */}
        {calendars.length > 0 && (
          <View style={[styles.field, showCalendarDropdown && styles.fieldDropdownOpen]}>
            <Text style={styles.label}>{t('calendar.eventForm.calendarLabel')}</Text>
            <TouchableOpacity
              style={[styles.selectButton, { flexDirection: rtl.rowDirection }, showCalendarDropdown && styles.selectButtonActive]}
              onPress={() => setShowCalendarDropdown(!showCalendarDropdown)}
              activeOpacity={0.7}
            >
              <View style={[styles.selectButtonContent, { flexDirection: rtl.rowDirection }]}>
                {selectedCalendarId && calendars.find((c: any) => c._id === selectedCalendarId) && (
                  <View style={[styles.calendarSelectorItem, { flexDirection: rtl.rowDirection }]}>
                    <View style={[
                      styles.calendarDot,
                      { backgroundColor: calendars.find((c: any) => c._id === selectedCalendarId)?.color || colors.primary }
                    ]} />
                    <Text style={styles.selectButtonText}>
                      {calendars.find((c: any) => c._id === selectedCalendarId)?.name || t('calendar.eventForm.selectCalendarPlaceholder')}
                    </Text>
                  </View>
                )}
                {!selectedCalendarId && (
                  <Text style={[styles.selectButtonText, styles.selectButtonPlaceholder]}>
                    {t('calendar.eventForm.selectCalendarPlaceholder')}
                  </Text>
                )}
              </View>
              <Ionicons 
                name={showCalendarDropdown ? 'chevron-up' : 'chevron-down'} 
                size={20} 
                color={colors.text.secondary} 
              />
            </TouchableOpacity>
            
            {showCalendarDropdown && (
              <View style={styles.calendarDropdown}>
                <ScrollView style={styles.calendarList} nestedScrollEnabled>
                  {calendars.map((cal: any) => {
                    const isSelected = selectedCalendarId === cal._id;
                    return (
                      <TouchableOpacity
                        key={cal._id}
                        style={[
                          styles.calendarItem,
                          { flexDirection: rtl.rowDirection },
                          isSelected && styles.calendarItemSelected
                        ]}
                        onPress={() => {
                          setSelectedCalendarId(cal._id);
                          setShowCalendarDropdown(false);
                          if (onCalendarChange) {
                            onCalendarChange(cal._id);
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={[styles.calendarDot, { backgroundColor: cal.color || colors.primary }]} />
                        <Text style={styles.calendarItemText}>{cal.name}</Text>
                        {isSelected && (
                          <Ionicons name="checkmark" size={20} color={colors.primary} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            )}
          </View>
        )}

        {/* Description */}
        <View style={styles.field}>
          <Input
            label={t('calendar.eventForm.descriptionLabel')}
            value={formData.description || ''}
            onChangeText={(text) => updateField('description', text)}
            placeholder={t('calendar.eventForm.descriptionPlaceholder')}
            multiline
            numberOfLines={4}
            maxLength={5000}
          />
        </View>

        {/* Contacts Selection */}
        <View style={[styles.field, showContactDropdown && styles.fieldDropdownOpen]}>
          <Text style={styles.label}>{t('calendar.eventForm.contactsLabel')}</Text>
          <TouchableOpacity
            style={[styles.selectButton, { flexDirection: rtl.rowDirection }, showContactDropdown && styles.selectButtonActive]}
            onPress={() => {
              setShowContactDropdown(!showContactDropdown);
              // Clear search when closing dropdown
              if (showContactDropdown) {
                setContactSearchQuery('');
              }
            }}
            activeOpacity={0.7}
          >
            <Text style={[
              styles.selectButtonText,
              selectedContactIds.length === 0 && styles.selectButtonPlaceholder
            ]}>
              {selectedContactIds.length === 0
                ? t('calendar.eventForm.selectContactsPlaceholder')
                : t('calendar.eventForm.contactsSelectedCount', {
                    count: selectedContactIds.length,
                    label: t(selectedContactIds.length > 1 ? 'calendar.eventForm.contactPlural' : 'calendar.eventForm.contactSingular'),
                  })}
            </Text>
            <Ionicons 
              name={showContactDropdown ? 'chevron-up' : 'chevron-down'} 
              size={20} 
              color={colors.text.secondary} 
            />
          </TouchableOpacity>
          
          {showContactDropdown && (
            <View style={styles.contactDropdown}>
              {/* Search Input */}
              <View style={[styles.contactSearchContainer, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="search" size={18} color={colors.text.secondary} style={rtl.marginEnd(spacing.xs)} />
                <TextInput
                  style={styles.contactSearchInput}
                  placeholder={t('calendar.eventForm.searchContactsPlaceholder')}
                  placeholderTextColor={colors.textLight}
                  value={contactSearchQuery}
                  onChangeText={setContactSearchQuery}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {contactSearchQuery.length > 0 && (
                  <TouchableOpacity
                    onPress={() => setContactSearchQuery('')}
                    style={[styles.contactSearchClear, rtl.marginStart(spacing.xs)]}
                  >
                    <Ionicons name="close-circle" size={18} color={colors.text.secondary} />
                  </TouchableOpacity>
                )}
              </View>
              
              <ScrollView style={styles.contactList} nestedScrollEnabled>
                {loadingContacts ? (
                  <View style={styles.contactLoading}>
                    <Text style={styles.contactLoadingText}>{t('calendar.eventForm.loadingContacts')}</Text>
                  </View>
                ) : (() => {
                  // Filter contacts based on search query
                  const filteredContacts = contactSearchQuery.trim()
                    ? contacts.filter((contact: any) => {
                        const query = contactSearchQuery.toLowerCase();
                        const name = String(contact.name || '').toLowerCase();
                        const email = firstText(contact.email).toLowerCase();
                        const phone = firstText(contact.phoneNumber).toLowerCase();
                        return name.includes(query) || email.includes(query) || phone.includes(query);
                      })
                    : contacts;
                  
                  if (filteredContacts.length === 0) {
                    return (
                      <View style={styles.contactEmpty}>
                        <Text style={styles.contactEmptyText}>
                          {contactSearchQuery.trim() ? t('calendar.eventForm.noContactsFound') : t('calendar.eventForm.noContactsAvailable')}
                        </Text>
                      </View>
                    );
                  }

                  return filteredContacts.map((contact: any) => {
                    const isSelected = selectedContactIds.includes(contact.id);
                    return (
                      <TouchableOpacity
                        key={contact.id}
                        style={[
                          styles.contactItem,
                          { flexDirection: rtl.rowDirection },
                          isSelected && styles.contactItemSelected
                        ]}
                        onPress={() => {
                          if (isSelected) {
                            setSelectedContactIds(selectedContactIds.filter(id => id !== contact.id));
                          } else {
                            setSelectedContactIds([...selectedContactIds, contact.id]);
                          }
                        }}
                        activeOpacity={0.7}
                      >
                        <View style={styles.contactCheckbox}>
                          {isSelected && (
                            <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
                          )}
                          {!isSelected && (
                            <Ionicons name="ellipse-outline" size={24} color={colors.border} />
                          )}
                        </View>
                        <View style={styles.contactInfo}>
                          <Text style={styles.contactName} numberOfLines={1}>
                            {contact.name || t('calendar.eventForm.unknownContact')}
                          </Text>
                          {(contact.email || contact.phoneNumber) && (
                            <Text style={styles.contactDetail} numberOfLines={1}>
                              {contact.email || contact.phoneNumber}
                            </Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  });
                })()}
              </ScrollView>
            </View>
          )}

          {selectedContactIds.length > 0 && (
            <View style={[styles.selectedContacts, { flexDirection: rtl.rowDirection }]}>
              {contacts
                .filter((c: any) => selectedContactIds.includes(c.id))
                .map((contact: any) => {
                  const existingStatus = existingAttendeesByContactId[contact.id]?.status;
                  return (
                    <View key={contact.id} style={styles.selectedContactColumn}>
                      <View style={[styles.selectedContactTag, { flexDirection: rtl.rowDirection }]}>
                        <Text style={styles.selectedContactText}>{contact.name || t('calendar.eventForm.unknownContactShort')}</Text>
                        <TouchableOpacity
                          onPress={() => setSelectedContactIds(selectedContactIds.filter(id => id !== contact.id))}
                          style={styles.selectedContactRemove}
                        >
                          <Ionicons name="close" size={16} color={colors.white} />
                        </TouchableOpacity>
                      </View>
                      {existingStatus && <AttendeeStatusBadge status={existingStatus} />}
                    </View>
                  );
                })}
            </View>
          )}
        </View>

        {/* All Day Toggle */}
        <View style={styles.field}>
          <Toggle
            label={t('calendar.eventForm.allDayEventLabel')}
            value={formData.isAllDay || false}
            onValueChange={(value) => updateField('isAllDay', value)}
          />
        </View>

        {/* Start & End Date/Time - Inline */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.eventForm.dateTimeLabel')}</Text>
          <View style={[styles.inlineDateFields, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.inlineDateField}>
              <DatePickerField
                label={formData.isAllDay ? t('calendar.eventForm.startDateLabel') : t('calendar.eventForm.startLabel')}
                value={formData.startDate}
                mode={formData.isAllDay ? 'date' : 'datetime'}
                onChange={(date) => {
                  if (!date) return;
                  const selectedDate = new Date(date);
                  if (formData.isAllDay) {
                    selectedDate.setHours(0, 0, 0, 0);
                  }
                  updateField('startDate', selectedDate);
                  // Ensure end date/time is after start date/time
                  if (selectedDate >= formData.endDate) {
                    const newEndDate = new Date(selectedDate);
                    newEndDate.setHours(newEndDate.getHours() + 1);
                    updateField('endDate', newEndDate);
                  }
                }}
              />
            </View>
            <View style={styles.inlineDateField}>
              <DatePickerField
                label={formData.isAllDay ? t('calendar.eventForm.endDateLabel') : t('calendar.eventForm.endLabel')}
                value={formData.endDate}
                mode={formData.isAllDay ? 'date' : 'datetime'}
                minimumDate={formData.startDate}
                onChange={(date) => {
                  if (!date) return;
                  const selectedDate = new Date(date);
                  if (formData.isAllDay) {
                    selectedDate.setHours(23, 59, 59, 999);
                  }
                  updateField('endDate', selectedDate);
                }}
              />
            </View>
          </View>
          {errors.endDate && <Text style={styles.errorText}>{errors.endDate}</Text>}
        </View>

        {/* Timezone removed - will use calendar's timezone */}

        {/* Meeting Type */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.eventForm.meetingTypeLabel')}</Text>
          <View style={styles.radioGroup}>
            {(['offline', 'online', 'hybrid'] as const).map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.radioOption,
                  { flexDirection: rtl.rowDirection },
                  formData.meetingType === type && styles.radioOptionSelected,
                ]}
                onPress={() => updateField('meetingType', type)}
              >
                <Ionicons
                  name={formData.meetingType === type ? 'radio-button-on' : 'radio-button-off'}
                  size={20}
                  color={formData.meetingType === type ? colors.primary : colors.textLight}
                />
                <Text style={styles.radioText}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Location (for offline/hybrid) */}
        {(formData.meetingType === 'offline' || formData.meetingType === 'hybrid') && (
          <>
            <View style={styles.field}>
              <Input
                label={t('calendar.eventForm.locationNameLabel')}
                value={formData.location?.name || ''}
                onChangeText={(text) => updateLocationField('name', text)}
                placeholder={t('calendar.eventForm.locationNamePlaceholder')}
              />
            </View>
            <View style={styles.field}>
              <Input
                label={t('calendar.eventForm.addressLabel')}
                value={formData.location?.address || ''}
                onChangeText={(text) => updateLocationField('address', text)}
                placeholder={t('calendar.eventForm.addressPlaceholder')}
                multiline
                numberOfLines={2}
              />
            </View>
          </>
        )}

        {/* Online Meeting (for online/hybrid) */}
        {(formData.meetingType === 'online' || formData.meetingType === 'hybrid') && (
          <>
            <View style={styles.field}>
              <Text style={styles.label}>{t('calendar.eventForm.meetingProviderLabel')}</Text>
              <View style={styles.radioGroup}>
                {(['zoom', 'google_meet', 'microsoft_teams', 'custom'] as const).map((provider) => (
                  <TouchableOpacity
                    key={provider}
                    style={[
                      styles.radioOption,
                      { flexDirection: rtl.rowDirection },
                      formData.onlineMeeting?.provider === provider && styles.radioOptionSelected,
                    ]}
                    onPress={() => updateOnlineMeetingField('provider', provider)}
                  >
                    <Ionicons
                      name={
                        formData.onlineMeeting?.provider === provider
                          ? 'radio-button-on'
                          : 'radio-button-off'
                      }
                      size={20}
                      color={
                        formData.onlineMeeting?.provider === provider
                          ? colors.primary
                          : colors.textLight
                      }
                    />
                    <Text style={styles.radioText}>
                      {provider.charAt(0).toUpperCase() + provider.slice(1).replace('_', ' ')}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.field}>
              <Input
                label={t('calendar.eventForm.meetingUrlLabel')}
                value={formData.onlineMeeting?.joinUrl || ''}
                onChangeText={(text) => updateOnlineMeetingField('joinUrl', text)}
                placeholder={t('calendar.eventForm.meetingUrlPlaceholder')}
                error={errors.onlineMeetingUrl}
                keyboardType="url"
                autoCapitalize="none"
              />
            </View>
            <View style={styles.field}>
              <Input
                label={t('calendar.eventForm.meetingPasswordLabel')}
                value={formData.onlineMeeting?.password || ''}
                onChangeText={(text) => updateOnlineMeetingField('password', text)}
                placeholder={t('calendar.eventForm.optionalPlaceholder')}
                secureTextEntry
              />
            </View>
          </>
        )}

        {/* Attachments */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.eventForm.attachmentsLabel')}</Text>
          <View style={styles.attachmentsContainer}>
            {formData.attachments && formData.attachments.length > 0 && (
              <View style={styles.attachmentsList}>
                {formData.attachments.map((attachment, index) => (
                  <View key={index} style={[styles.attachmentItem, { flexDirection: rtl.rowDirection }]}>
                    <Ionicons
                      name={attachment.type?.startsWith('image/') ? 'image-outline' :
                            attachment.type?.startsWith('video/') ? 'videocam-outline' :
                            attachment.type?.includes('pdf') ? 'document-text-outline' :
                            'document-outline'}
                      size={20}
                      color={colors.primary}
                    />
                    <View style={styles.attachmentInfo}>
                      <Text style={styles.attachmentName} numberOfLines={1}>
                        {attachment.name}
                      </Text>
                      {attachment.size && (
                        <Text style={styles.attachmentSize}>
                          {(attachment.size / 1024).toFixed(2)} KB
                        </Text>
                      )}
                    </View>
                    <TouchableOpacity
                      onPress={async () => {
                        const url = await getAttachmentUrl(attachment.url);
                        if (Platform.OS === 'web') {
                          window.open(url, '_blank');
                        } else {
                          Linking.openURL(url);
                        }
                      }}
                      style={styles.attachmentAction}
                    >
                      <Ionicons name="open-outline" size={18} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => handleRemoveAttachment(index)}
                      style={styles.attachmentAction}
                    >
                      <Ionicons name="trash-outline" size={18} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
            <TouchableOpacity
              style={[styles.addAttachmentButton, { flexDirection: rtl.rowDirection }]}
              onPress={handlePickAttachment}
              disabled={uploadingAttachment}
              activeOpacity={0.7}
            >
              {uploadingAttachment ? (
                <Text style={styles.addAttachmentText}>{t('calendar.eventForm.uploadingText')}</Text>
              ) : (
                <>
                  <Ionicons name="attach-outline" size={20} color={colors.primary} />
                  <Text style={styles.addAttachmentText}>{t('calendar.eventForm.addAttachmentButton')}</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {/* Notes */}
        <View style={styles.field}>
          <Text style={styles.label}>{t('calendar.eventForm.notesLabel')}</Text>
          {formData.notes && formData.notes.length > 0 && (
            <View style={styles.notesList}>
              {formData.notes.map((note, index) => (
                <View key={index} style={styles.noteItem}>
                  <Input
                    value={note.content}
                    onChangeText={(text) => handleUpdateNote(index, text)}
                    multiline
                    numberOfLines={3}
                    placeholder={t('calendar.eventForm.noteContentPlaceholder')}
                    style={rtl.paddingEnd(40)}
                  />
                  <TouchableOpacity
                    onPress={() => handleRemoveNote(index)}
                    style={[styles.noteRemoveButton, rtl.isRTL ? { left: spacing.sm } : { right: spacing.sm }]}
                  >
                    <Ionicons name="trash-outline" size={18} color={colors.error} />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}
          <View style={[styles.addNoteContainer, { flexDirection: rtl.rowDirection }]}>
            <Input
              value={newNote}
              onChangeText={setNewNote}
              multiline
              numberOfLines={3}
              placeholder={t('calendar.eventForm.addNotePlaceholder')}
              style={styles.newNoteInput}
            />
            <TouchableOpacity
              style={styles.addNoteButton}
              onPress={handleAddNote}
              disabled={!newNote.trim()}
              activeOpacity={0.7}
            >
              <Ionicons name="add" size={20} color={newNote.trim() ? colors.primary : colors.text.secondary} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Reminders */}
        <ReminderPicker
          value={formData.reminders || []}
          onChange={(reminders) => updateField('reminders', reminders)}
        />

        {/* Action Buttons */}
        {!hideActions && (
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            <Button
              label={t('calendar.eventForm.cancelButton')}
              onPress={onCancel}
              variant="outline"
              style={styles.cancelButton}
              disabled={loading}
            />
            <Button
              label={resolvedSubmitLabel}
              onPress={handleSubmit}
              loading={loading}
              style={styles.submitButton}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
});

EventForm.displayName = 'EventForm';

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: spacing.md,
  },
  form: {
    gap: spacing.md,
  },
  field: {
    marginBottom: spacing.md,
    position: 'relative',
    zIndex: 1,
  },
  fieldDropdownOpen: {
    zIndex: 9999,
  },
  label: {
    ...typography.body,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  errorText: {
    ...typography.caption,
    color: colors.error,
    marginTop: spacing.xs,
  },
  helperText: {
    ...typography.caption,
    color: colors.textLight,
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  radioGroup: {
    gap: spacing.sm,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    gap: spacing.sm,
  },
  radioOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  radioText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  cancelButton: {
    flex: 1,
  },
  submitButton: {
    flex: 1,
  },
  inlineDateFields: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  inlineDateField: {
    flex: 1,
  },
  attachmentsContainer: {
    gap: spacing.sm,
  },
  attachmentsList: {
    gap: spacing.xs,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.sm,
  },
  attachmentInfo: {
    flex: 1,
    gap: spacing.xs / 2,
  },
  attachmentName: {
    ...typography.body,
    color: colors.text.primary,
    fontSize: typography.fontSize.sm,
  },
  attachmentSize: {
    ...typography.caption,
    color: colors.textLight,
    fontSize: typography.fontSize.xs,
  },
  attachmentAction: {
    padding: spacing.xs,
  },
  addAttachmentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    gap: spacing.xs,
  },
  addAttachmentText: {
    ...typography.body,
    color: colors.primary,
    fontSize: typography.fontSize.sm,
  },
  notesList: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  noteItem: {
    position: 'relative',
  },
  noteRemoveButton: {
    position: 'absolute',
    top: spacing.sm,
    padding: spacing.xs,
  },
  addNoteContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  newNoteInput: {
    flex: 1,
  },
  addNoteButton: {
    padding: spacing.sm,
    marginTop: spacing.xs,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    minHeight: 48,
  },
  selectButtonActive: {
    borderColor: colors.primary,
  },
  selectButtonText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
  },
  selectButtonPlaceholder: {
    color: colors.textLight,
  },
  contactDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.surface,
    maxHeight: 300,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 20,
    zIndex: 10000,
  },
  contactSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.primary,
  },
  contactSearchInput: {
    flex: 1,
    ...typography.body,
    color: colors.text.primary,
    padding: spacing.xs,
    fontSize: typography.fontSize.sm,
  },
  contactSearchClear: {
    padding: spacing.xs / 2,
  },
  contactList: {
    maxHeight: 250,
  },
  contactLoading: {
    padding: spacing.md,
    alignItems: 'center',
  },
  contactLoadingText: {
    ...typography.body,
    color: colors.textLight,
  },
  contactEmpty: {
    padding: spacing.md,
    alignItems: 'center',
  },
  contactEmptyText: {
    ...typography.body,
    color: colors.textLight,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.sm,
  },
  contactItemSelected: {
    backgroundColor: colors.primaryLight,
  },
  contactCheckbox: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contactInfo: {
    flex: 1,
    gap: spacing.xs,
  },
  contactName: {
    ...typography.body,
    color: colors.text.primary,
    fontWeight: '500',
  },
  contactDetail: {
    ...typography.caption,
    color: colors.textLight,
  },
  selectedContacts: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  selectedContactColumn: {
    alignItems: 'flex-start',
  },
  selectedContactTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 16,
    gap: spacing.xs,
  },
  selectedContactText: {
    ...typography.caption,
    color: colors.white,
    fontWeight: '500',
  },
  selectedContactRemove: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: withAlpha(colors.white, 0.25),
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectButtonContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  calendarSelectorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  calendarDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  calendarDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.surface,
    maxHeight: 300,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 20,
    zIndex: 10000,
  },
  calendarList: {
    maxHeight: 300,
  },
  calendarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.sm,
  },
  calendarItemSelected: {
    backgroundColor: colors.primaryLight,
  },
  calendarItemText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
  },
});

